import { createSlice, createSelector, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { IntegrationStatus } from 'features/client-integrations/integration-setup/integration-tile';

export interface IntegrationMeta {
  name: string;
  logo: string;
  status: IntegrationStatus;
}

export type ClientIntegrationKey =
  | 'GoogleAds'
  | 'FacebookAds'
  | 'BingAds'
  | 'LinkedInAds'
  | 'PinterestAds'
  | 'SnapchatAds'
  | 'TikTokAds'
  | 'TheTradeDeskAds'
  | 'TaboolaAds'
  | 'StackAdaptAds'
  | 'OutbrainAds'
  | 'AwinAds';

export type ClientIntegrationMetaState = {
  [key in ClientIntegrationKey]: IntegrationMeta;
};

// once we have added a new DirectAPI integration,
// the default status should be set to NOT_CONFIGURED
// so it can be available for enabling by default.
const initialState: ClientIntegrationMetaState = {
  GoogleAds: {
    name: 'Google Ads',
    logo: '/media/logos/google-ads-logo.png',
    status: IntegrationStatus.NOT_CONFIGURED
  },
  FacebookAds: {
    name: 'Facebook Ads',
    logo: '/media/logos/facebook-logo.png',
    status: IntegrationStatus.NOT_CONFIGURED
  },
  BingAds: {
    name: 'Microsoft Ads',
    logo: '/media/logos/bing-logo.png',
    status: IntegrationStatus.COMING_SOON
  },
  LinkedInAds: {
    name: 'LinkedIn Ads',
    logo: '/media/logos/linkedin-logo.png',
    status: IntegrationStatus.COMING_SOON
  },
  PinterestAds: {
    name: 'Pinterest Ads',
    logo: '/media/logos/pinterest-logo.png',
    status: IntegrationStatus.COMING_SOON
  },
  SnapchatAds: {
    name: 'Snapchat Ads',
    logo: '/media/logos/snapchat-logo.png',
    status: IntegrationStatus.COMING_SOON
  },
  TikTokAds: {
    name: 'TikTok Ads',
    logo: '/media/logos/tiktok-logo.png',
    status: IntegrationStatus.COMING_SOON
  },
  TheTradeDeskAds: {
    name: 'The Trade Desk',
    logo: '/media/logos/thetradedesk-logo.png',
    status: IntegrationStatus.COMING_SOON
  },
  TaboolaAds: {
    name: 'Taboola',
    logo: '/media/logos/taboola-logo.png',
    status: IntegrationStatus.COMING_SOON
  },
  StackAdaptAds: {
    name: 'Stack Adapt',
    logo: '/media/logos/stackadapt-logo.png',
    status: IntegrationStatus.COMING_SOON
  },
  OutbrainAds: {
    name: 'Outbrain',
    logo: '/media/logos/outbrain-logo.png',
    status: IntegrationStatus.COMING_SOON
  },
  AwinAds: {
    name: 'Awin',
    logo: '/media/logos/awin-logo.png',
    status: IntegrationStatus.COMING_SOON
  }
};

const clientIntegrationMetaSlice = createSlice({
  name: 'clientIntegrationMeta',
  initialState,
  reducers: {
    setIntegrationStatus: (
      state,
      {
        payload: { key, status }
      }: PayloadAction<{
        key: ClientIntegrationKey;
        status: IntegrationStatus;
      }>
    ) => {
      if (state[key]) {
        state[key].status = status;
      }
    }
  }
});

export const selectClientIntegrationMeta = (state: RootState) =>
  state.clientIntegrationMeta;

export const selectAllIntegrations = createSelector(
  selectClientIntegrationMeta,
  (meta) => Object.entries(meta).map(([key, value]) => ({ key, ...value }))
);

// not used yet but could be handy later
export const selectIntegrationByKey = (key: ClientIntegrationKey) =>
  createSelector(selectClientIntegrationMeta, (meta) => meta[key]);

export const selectIntegrationMetaMap = createSelector(
  [selectAllIntegrations],
  (integrations) => {
    return integrations.reduce<Record<string, IntegrationMeta>>(
      (acc, integration) => {
        acc[integration.key] = integration;
        return acc;
      },
      {}
    );
  }
);

export const selectAvailableIntegrations = (connected: string[]) =>
  createSelector(selectAllIntegrations, (all) => {
    return all.filter((integration) => {
      const clean = integration.name.replace(/\s+/g, '').toLowerCase();
      return !connected.some(
        (ci) => ci.replace(/\s+/g, '').toLowerCase() === clean
      );
    });
  });

export const { setIntegrationStatus } = clientIntegrationMetaSlice.actions;

export const clientIntegrationMetaReducer = clientIntegrationMetaSlice.reducer;

export default clientIntegrationMetaReducer;
